import { AitCellData } from "aic";
import { IEditorV3, stringToV3 } from "editor-v3";

/**
 * Ensure an editor input has at least one text block and normalised lines.
 *
 * @param input - Optional editor input
 * @returns Normalised IEditorV3 instance
 */
function processInput(input?: IEditorV3): IEditorV3 {
  if (!input) return stringToV3("");
  input.lines =
    !input.lines || input.lines.length === 0
      ? [{ textBlocks: [{ text: "", type: "text" }] }]
      : input.lines
          .filter(
            (line, ix) =>
              (ix === 0 && input.lines.every((l) => !l.textBlocks || l.textBlocks.length === 0)) ||
              (line.textBlocks && line.textBlocks.length > 0),
          )
          .map((line) => {
            if (line.textBlocks.length === 0) line.textBlocks.push({ text: "", type: "text" });
            return line;
          });
  return input;
}

/**
 * Process text for an array of cells.
 *
 * @param cells - Cells to process
 * @returns Cells with normalised text content
 */
const processCellsText = (cells: AitCellData<IEditorV3>[]) =>
  cells.map((cell) => ({
    ...cell,
    text: processInput(cell.text),
  }));

/**
 * Recursively process any editor text within a body object (cells, comments etc.).
 *
 * @param body - Object that may contain editor text
 * @returns The same object with text fields normalised
 */
export function handleCellText<T>(body: T): T {
  if (body === null || body === undefined || typeof body !== "object") return body as T;
  const obj = body as { [key: string | number]: unknown };
  Object.keys(obj).forEach((key: string | number) => {
    const value = obj[key];
    // Update cells text
    if (key === "cells") {
      obj[key] = processCellsText(value as AitCellData<IEditorV3>[]);
    }
    // Update comment text
    else if (
      key === "comments" &&
      typeof value === "object" &&
      value !== null &&
      "lines" in value
    ) {
      obj[key] = processInput(value as IEditorV3);
    }
    // Recurse objects
    else if (value && typeof value === "object") {
      obj[key] = handleCellText(value as { [key: string]: unknown });
    }
  });
  return body as T;
}
